import React, { useState, useEffect } from 'react';
import {
  Database, RefreshCw, Play, XCircle, Clock, CheckCircle,
  AlertTriangle, ChevronDown, ChevronUp, Loader2
} from 'lucide-react';
import migrationService from '../services/migrationService';
import Notification from '../components/common/Notification';
import ConfirmDialog from '../components/common/ConfirmDialog';
import StartMigrationModal from '../components/migrations/StartMigrationModal';

const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { bg: 'bg-gray-100', text: 'text-gray-700', icon: Clock },
    downloading: { bg: 'bg-primary-100', text: 'text-primary-700', icon: Loader2 },
    running: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Loader2 },
    completed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
    failed: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
    cancelled: { bg: 'bg-gray-100', text: 'text-gray-500', icon: XCircle }
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className={`h-3 w-3 mr-1 ${status === 'downloading' || status === 'running' ? 'animate-spin' : ''}`} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const ProgressBar = ({ progress }) => {
  const phases = Object.entries(progress || {});
  if (phases.length === 0) return null;

  const totalItems = phases.reduce((sum, [_, p]) => sum + (p.total || 0), 0);
  const processedItems = phases.reduce((sum, [_, p]) => sum + (p.processed || 0), 0);
  const percent = totalItems > 0 ? Math.round((processedItems / totalItems) * 100) : 0;

  return (
    <div className="mt-2">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>{processedItems} / {totalItems} items</span>
        <span>{percent}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-primary-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};

const MigrationRow = ({ job, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'warning', confirmText: 'Confirm' });

  const loadDetails = async () => {
    if (details) return;
    setLoading(true);
    try {
      const data = await migrationService.getMigration(job.id);
      setDetails(data);
    } catch (err) {
      console.error('Failed to load migration details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExpand = () => {
    if (!expanded) loadDetails();
    setExpanded(!expanded);
  };

  const handleCancel = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Cancel Migration',
      message: 'Are you sure you want to cancel this migration?',
      variant: 'warning',
      confirmText: 'Cancel Migration',
      onConfirm: async () => {
        try {
          await migrationService.cancelMigration(job.id);
          onRefresh();
        } catch (err) {
          console.error('Failed to cancel migration:', err);
        }
      },
    });
  };

  const handleRetry = async () => {
    try {
      await migrationService.retryMigration(job.id);
      onRefresh();
    } catch (err) {
      console.error('Failed to retry migration:', err);
    }
  };

  const isRunning = ['pending', 'downloading', 'running'].includes(job.status);
  const canRetry = ['failed', 'cancelled'].includes(job.status);

  return (
    <>
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      {/* Summary Row */}
      <div
        role="button"
        tabIndex={0}
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
        onClick={handleExpand}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleExpand(); } }}
      >
        <div className="flex items-center space-x-4">
          <Database className="h-5 w-5 text-gray-400" />
          <div>
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-900">
                {job.source_type.toUpperCase()} Import
              </span>
              <StatusBadge status={job.status} />
              {job.dry_run && (
                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                  Dry Run
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500">
              {new Date(job.created_at).toLocaleString()}
              {job.created_by_name && ` by ${job.created_by_name}`}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Stats */}
          {job.stats && Object.keys(job.stats).length > 0 && (
            <div className="hidden md:flex space-x-4 text-sm text-gray-500">
              {job.stats.clients_imported && (
                <span>{job.stats.clients_imported} clients</span>
              )}
              {job.stats.cases_imported && (
                <span>{job.stats.cases_imported} cases</span>
              )}
              {job.stats.documents_imported && (
                <span>{job.stats.documents_imported} docs</span>
              )}
            </div>
          )}

          {/* Error/Warning counts */}
          {(job.error_count > 0 || job.warning_count > 0) && (
            <div className="flex items-center space-x-2">
              {job.error_count > 0 && (
                <span className="text-red-600 text-sm flex items-center">
                  <XCircle className="h-4 w-4 mr-1" />
                  {job.error_count}
                </span>
              )}
              {job.warning_count > 0 && (
                <span className="text-yellow-600 text-sm flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  {job.warning_count}
                </span>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
            {isRunning && (
              <button
                onClick={handleCancel}
                className="text-red-600 hover:text-red-800 p-1"
                title="Cancel"
              >
                <XCircle className="h-5 w-5" />
              </button>
            )}
            {canRetry && (
              <button
                onClick={handleRetry}
                className="text-primary-600 hover:text-primary-800 p-1"
                title="Retry"
              >
                <RefreshCw className="h-5 w-5" />
              </button>
            )}
          </div>

          {expanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Progress Bar (if running) */}
      {isRunning && (
        <div className="px-4 pb-4">
          <ProgressBar progress={job.progress} />
          {job.current_phase && (
            <div className="text-xs text-gray-500 mt-1">
              Current phase: {job.current_phase}
            </div>
          )}
        </div>
      )}

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          {loading ? (
            <div className="text-center py-4">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
            </div>
          ) : details ? (
            <div className="space-y-4">
              {/* Statistics */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Statistics</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  {Object.entries(details.stats || {}).map(([key, value]) => (
                    <div key={key} className="bg-white p-2 rounded border">
                      <div className="text-gray-500">{key.replace(/_/g, ' ')}</div>
                      <div className="font-semibold">{value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Errors */}
              {details.errors && details.errors.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-600 mb-2">
                    Errors ({details.errors.length})
                  </h4>
                  <div className="max-h-40 overflow-y-auto bg-red-50 rounded p-2 text-sm">
                    {details.errors.map((err, i) => (
                      <div key={i} className="text-red-800 py-1">
                        {err.phase && <span className="font-medium">[{err.phase}]</span>} {err.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warnings */}
              {details.warnings && details.warnings.length > 0 && (
                <div>
                  <h4 className="font-medium text-yellow-600 mb-2">
                    Warnings ({details.warnings.length})
                  </h4>
                  <div className="max-h-40 overflow-y-auto bg-yellow-50 rounded p-2 text-sm">
                    {details.warnings.map((warn, i) => (
                      <div key={i} className="text-yellow-800 py-1">
                        {warn.phase && <span className="font-medium">[{warn.phase}]</span>} {warn.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Timing */}
              <div className="text-sm text-gray-500">
                {details.started_at && (
                  <div>Started: {new Date(details.started_at).toLocaleString()}</div>
                )}
                {details.completed_at && (
                  <div>Completed: {new Date(details.completed_at).toLocaleString()}</div>
                )}
                {details.duration_seconds && (
                  <div>Duration: {Math.floor(details.duration_seconds / 60)}m {details.duration_seconds % 60}s</div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-gray-500">No details available</div>
          )}
        </div>
      )}
    </div>

    <ConfirmDialog
      isOpen={confirmDialog.isOpen}
      onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      onConfirm={confirmDialog.onConfirm}
      title={confirmDialog.title}
      message={confirmDialog.message}
      variant={confirmDialog.variant}
      confirmText={confirmDialog.confirmText}
    />
    </>
  );
};

const Migrations = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStartModal, setShowStartModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [notification, setNotification] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info'
  });

  // For manual refresh
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshJobs = () => setRefreshKey(k => k + 1);

  // Initial fetch and polling
  useEffect(() => {
    let isMounted = true;
    let pollTimeout = null;

    const doFetch = async () => {
      if (!isMounted) return;
      try {
        const data = await migrationService.listMigrations(1, 50, statusFilter);
        if (!isMounted) return;
        setJobs(data.jobs);
        setLoading(false);

        // Schedule next poll if there are running jobs
        const hasRunning = data.jobs.some(j =>
          ['pending', 'downloading', 'running'].includes(j.status)
        );
        if (hasRunning && isMounted) {
          pollTimeout = setTimeout(doFetch, 5000);
        }
      } catch (error) {
        console.error('Error fetching migrations:', error);
        if (isMounted) {
          setNotification({
            isOpen: true,
            title: 'Error',
            message: 'Failed to fetch migration jobs',
            variant: 'error'
          });
          setLoading(false);
        }
      }
    };

    doFetch();

    return () => {
      isMounted = false;
      if (pollTimeout) clearTimeout(pollTimeout);
    };
  }, [statusFilter, refreshKey]);

  const handleStartSuccess = (result) => {
    setNotification({
      isOpen: true,
      title: 'Success',
      message: result.message || 'Migration started successfully',
      variant: 'success'
    });
    refreshJobs();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Data Migrations</h1>
          <p className="mt-1 text-sm text-gray-500">
            Import data from Clio, MyCase, and other systems
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={refreshJobs}
            className="p-2 text-gray-500 hover:text-gray-700"
            title="Refresh"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
          <button
            onClick={() => setShowStartModal(true)}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            <Play className="h-5 w-5 mr-2" />
            Start Migration
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex space-x-2">
        <button
          onClick={() => setStatusFilter(null)}
          className={`px-3 py-1 rounded-full text-sm ${
            !statusFilter ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        {['running', 'completed', 'failed'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-1 rounded-full text-sm ${
              statusFilter === status ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Jobs List */}
      <div className="space-y-3">
        {jobs.map((job) => (
          <MigrationRow key={job.id} job={job} onRefresh={refreshJobs} />
        ))}
      </div>

      {jobs.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Database className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No migrations</h3>
          <p className="mt-1 text-sm text-gray-500">
            Start a migration to import data from another system.
          </p>
          <div className="mt-6">
            <button
              onClick={() => setShowStartModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              <Play className="h-5 w-5 mr-2" />
              Start Migration
            </button>
          </div>
        </div>
      )}

      {/* Start Migration Modal */}
      <StartMigrationModal
        isOpen={showStartModal}
        onClose={() => setShowStartModal(false)}
        onSuccess={handleStartSuccess}
      />

      {/* Notification */}
      <Notification
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
      />
    </div>
  );
};

export default Migrations;
